import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

/**
 * Reusable Dropdown Menu Component
 * 
 * Features:
 * - Click-outside handling
 * - Keyboard navigation (ESC to close)
 * - Support for icons, badges, dividers
 * - Dark mode support
 * - Mobile responsive
 */
const DropdownMenu = ({
    trigger,
    items = [],
    position = 'bottom-right',
    className = '',
    showChevron = true,
    onOpenChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                if (onOpenChange) onOpenChange(false);
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                if (onOpenChange) onOpenChange(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onOpenChange]);

    const toggleDropdown = () => {
        const newState = !isOpen;
        setIsOpen(newState);
        if (onOpenChange) onOpenChange(newState);
    };

    const handleItemClick = (item) => {
        if (item.onClick) {
            item.onClick();
        } else if (item.path) {
            navigate(item.path);
        }
        setIsOpen(false);
        if (onOpenChange) onOpenChange(false);
    };

    const positionClasses = {
        'bottom-right': 'right-0 top-full mt-2',
        'bottom-left': 'left-0 top-full mt-2',
        'top-right': 'right-0 bottom-full mb-2',
        'top-left': 'left-0 bottom-full mb-2',
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                onClick={toggleDropdown}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-plantlix-500/10 dark:hover:bg-plantlix-900/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-plantlix-500/60"
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                {trigger}
                {showChevron && (
                    <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                )}
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className={`absolute ${positionClasses[position]} min-w-[240px] bg-white/95 dark:bg-[#1a1a1a]/95 backdrop-blur-xl border border-gray-200/80 dark:border-white/10 rounded-2xl shadow-2xl shadow-black/10 dark:shadow-black/30 p-2 z-[100] animate-in fade-in slide-in-from-top-2 duration-200`}
                    role="menu"
                >
                    {items.map((item, index) => {
                        // Divider
                        if (item.type === 'divider') {
                            return (
                                <div
                                    key={`divider-${index}`}
                                    className="my-2 border-t border-gray-200 dark:border-gray-700"
                                    role="separator"
                                />
                            );
                        }

                        // Menu Item
                        const Icon = item.icon;
                        const isDestructive = item.variant === 'danger';

                        return (
                            <button
                                key={item.key || index}
                                onClick={() => { if (!item.disabled) handleItemClick(item); }}
                                title={item.tooltip}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-150 ${item.disabled
                                    ? 'opacity-50 cursor-not-allowed text-gray-500 dark:text-gray-400'
                                    : isDestructive
                                        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                        : 'text-gray-700 dark:text-gray-200 hover:bg-plantlix-50 dark:hover:bg-plantlix-900/20 hover:text-plantlix-700 dark:hover:text-plantlix-300'
                                    }`}
                                role="menuitem"
                            >
                                {Icon && (
                                    <Icon className={`w-5 h-5 flex-shrink-0 ${item.disabled
                                        ? 'text-gray-400 dark:text-gray-600'
                                        : isDestructive
                                            ? 'text-red-500 dark:text-red-400'
                                            : 'text-gray-500 dark:text-gray-400'
                                        }`} />
                                )}

                                <span className="flex-1 font-medium text-sm">
                                    {item.label}
                                </span>

                                {item.badge && (
                                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                                        {item.badge}
                                    </span>
                                )}

                                {item.shortcut && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                                        {item.shortcut}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DropdownMenu;
